import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Calendar, Users, MapPin, Globe, Clock, Building2, ArrowRight,
  CheckCircle, Ticket, Share2, ExternalLink, Lock, ArrowLeft,
  Mail, Phone, GraduationCap, ChevronRight, Tag, Eye
} from 'lucide-react';
import axios from 'axios';
import { PriceBreakdown } from '../components/events/PriceBreakdown';
import { toast } from 'sonner';
import EventTicket from '../components/EventTicket';
import { EventTeamPanel } from '../components/events/EventTeamPanel';
import { HackathonShowcase } from '../components/events/HackathonShowcase';

const API = process.env.REACT_APP_BACKEND_URL;

/* Lightweight Markdown-ish Text Renderer */
const FormattedText = ({ text, className = '', darkMode = false }) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let currentList = [];
  let key = 0;

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`ul-${key++}`} className={`list-disc pl-5 space-y-1 mb-4 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          {currentList.map((item, i) => <li key={i} className="leading-relaxed">{parseLine(item)}</li>)}
        </ul>
      );
      currentList = [];
    }
  };

  const parseLine = (line) => {
    const parts = [];
    let remaining = line;
    let partKey = 0;
    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      if (boldMatch) {
        const idx = remaining.indexOf(boldMatch[0]);
        if (idx > 0) parts.push(<span key={partKey++}>{remaining.slice(0, idx)}</span>);
        parts.push(<strong key={partKey++} className="font-semibold">{boldMatch[1]}</strong>);
        remaining = remaining.slice(idx + boldMatch[0].length);
      } else {
        parts.push(<span key={partKey++}>{remaining}</span>);
        break;
      }
    }
    return parts.length === 1 ? parts[0] : <>{parts}</>;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (trimmed === '') { flushList(); elements.push(<div key={`br-${key++}`} className="h-3" />); continue; }
    if (trimmed.startsWith('## ')) { flushList(); elements.push(<h3 key={`h3-${key++}`} className={`text-lg font-bold mb-2 mt-4 ${darkMode ? 'text-white' : 'text-slate-800'}`}>{parseLine(trimmed.slice(3))}</h3>); continue; }
    if (trimmed.startsWith('# ')) { flushList(); elements.push(<h2 key={`h2-${key++}`} className={`text-xl font-bold mb-3 mt-5 ${darkMode ? 'text-white' : 'text-slate-900'}`}>{parseLine(trimmed.slice(2))}</h2>); continue; }
    if (/^[-*]\s/.test(trimmed)) { currentList.push(trimmed.replace(/^[-*]\s+/, '')); continue; }
    if (/^\d+[.)]\s/.test(trimmed)) { flushList(); elements.push(<p key={`p-${key++}`} className={`leading-relaxed mb-1 pl-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{parseLine(trimmed)}</p>); continue; }
    flushList();
    elements.push(<p key={`p-${key++}`} className={`leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{parseLine(trimmed)}</p>);
  }
  flushList();
  return <div className={className}>{elements}</div>;
};

const EventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [hasTeam, setHasTeam] = useState(false);
  const [regData, setRegData] = useState(null);
  const [ticketReg, setTicketReg] = useState(null);

  useEffect(() => {
    fetchEvent();
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('order_id');
    const status = params.get('status');
    if (orderId && status === 'success') verifyPayment(orderId);
  }, [eventId]);

  // Fetch full registration for the ticket once we have a registration id
  useEffect(() => {
    if (registered && regData?.id) {
      axios.get(`${API}/api/public/events/${eventId}/ticket/${regData.id}`)
        .then((res) => setTicketReg(res.data.registration))
        .catch(() => {});
    }
  }, [registered, regData, eventId]);

  const verifyPayment = async (orderId) => {
    try {
      const res = await axios.post(`${API}/api/public/events/${eventId}/verify-payment?order_id=${orderId}`);
      if (res.data.status === 'success') {
        setRegistered(true);
        setRegData({ id: res.data.registration_id, payment_status: 'paid' });
        toast.success('Payment successful! You are registered.');
        window.history.replaceState({}, '', `/events/${eventId}`);
      } else {
        toast.info('Payment is being processed.');
      }
    } catch { toast.error('Could not verify payment'); }
  };

  const fetchEvent = async () => {
    try {
      const res = await axios.get(`${API}/api/public/events/${eventId}`);
      setEvent(res.data);
      document.title = `${res.data.title} | EvalentX Events`;
      updateMetaTags(res.data);
    } catch {
      // Event under re-review: existing team members can still manage their team
      const tok = localStorage.getItem('codearena_token');
      try {
        if (!tok) throw new Error('no token');
        const r = await axios.get(`${API}/api/event-teams/${eventId}/my-team`, { headers: { Authorization: `Bearer ${tok}` } });
        if (!r.data.team) throw new Error('no team');
        setEvent({ ...r.data.event, under_review: true });
      } catch { toast.error('Event not found'); }
    }
    finally { setLoading(false); }
  };

  const updateMetaTags = (evt) => {
    const setMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(property.startsWith('og:') ? 'property' : 'name', property); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const desc = evt.description?.slice(0, 160) || `${evt.title} hosted on EvalentX`;
    const url = `${window.location.origin}/events/${evt.id}`;
    setMeta('og:title', evt.title); setMeta('og:description', desc); setMeta('og:url', url);
    setMeta('og:type', 'website'); setMeta('og:image', evt.cover_image || `${window.location.origin}/og-default.png`);
    setMeta('twitter:card', 'summary_large_image'); setMeta('twitter:title', evt.title); setMeta('twitter:description', desc);
  };

  const isTeam = event?.registration_mode === 'team';
  const teamCta = hasTeam ? 'View My Team' : 'Register Your Team';
  const openRegister = () => {
    if (isTeam) document.getElementById('team-registration')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else setShowRegister(true);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/events/${event.id}`;
    const text = `${event.title} - ${event.is_paid ? `INR ${event.price}` : 'Free'} | EvalentX`;
    if (navigator.share) { try { await navigator.share({ title: event.title, text, url }); } catch {} }
    else { await navigator.clipboard.writeText(url); toast.success('Link copied!'); }
  };

  const formatDate = (d) => {
    if (!d) return 'TBD';
    try { return new Date(d).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const formatShortDate = (d) => {
    if (!d) return 'TBD';
    try { return new Date(d).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!event) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA] gap-4">
      <h2 className="text-xl font-semibold text-[#0A0A0A]">Event not found</h2>
      <Link to="/codearena/events" className="text-[#002FA7] font-medium text-sm">Browse Events</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="event-page">
      {/* Hero Banner */}
      <div className="relative overflow-hidden bg-[#0A0A0A]">
        {/* Background image */}
        {event.cover_image && (
          <img src={event.cover_image} alt={event.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-12 md:pt-12 md:pb-20">
          {/* Back link */}
          <Link to="/codearena/events" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-8 transition-colors">
            <ArrowLeft size={16} /> All Events
          </Link>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="flex-1 max-w-2xl">
              {/* Company info with logo */}
              <div className="flex items-center gap-3 mb-5">
                {event.company_logo ? (
                  <img src={event.company_logo} alt={event.company_name} className="w-10 h-10 rounded-lg object-contain bg-white p-1" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center">
                    <Building2 size={18} className="text-white/70" />
                  </div>
                )}
                <div>
                  <p className="text-white/90 font-medium text-sm">{event.company_name}</p>
                  <p className="text-white/50 text-xs">Organizer</p>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {event.categories?.map((c, i) => (
                  <span key={i} className="px-2.5 py-1 bg-white/10 border border-white/10 rounded text-[11px] font-medium text-white/70 uppercase tracking-[0.08em]">{c}</span>
                ))}
                <span className={`px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-[0.08em] ${
                  event.is_paid ? 'bg-[#FFD600]/20 text-[#FFD600] border border-[#FFD600]/30' : 'bg-emerald-500/20 text-emerald-400 border border-emerald-400/30'
                }`}>
                  {event.is_paid ? `INR ${event.price?.toLocaleString()}` : 'Free Entry'}
                </span>
                {event.event_type && (
                  <span className="px-2.5 py-1 bg-[#002FA7]/30 border border-[#002FA7]/40 rounded text-[11px] font-medium text-blue-300 uppercase tracking-[0.08em]">
                    {event.event_type === 'offline' ? 'In-Person' : 'Online'}
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-[1.1] mb-4" data-testid="event-title">
                {event.title}
              </h1>

              {/* Quick info row */}
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/60 mb-6">
                <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#002FA7]" />{formatShortDate(event.event_date)}</span>
                <span className="flex items-center gap-1.5">
                  {event.event_type === 'offline' ? <MapPin size={14} className="text-[#002FA7]" /> : <Globe size={14} className="text-[#002FA7]" />}
                  {event.event_type === 'offline' ? event.venue || 'Venue TBD' : 'Online'}
                </span>
                <span className="flex items-center gap-1.5"><Users size={14} className="text-[#002FA7]" />{event.registration_count || 0} registered</span>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col items-start md:items-end gap-3">
              {!registered ? (
                <button onClick={openRegister}
                  className="group inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold px-8 py-3.5 rounded transition-all text-base"
                  data-testid="register-now-btn">
                  {isTeam ? teamCta : 'Register Now'} <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
              ) : (
                <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30 text-emerald-400 font-semibold px-6 py-3 rounded">
                  <CheckCircle size={18} /> Registered
                </div>
              )}
              <button onClick={handleShare}
                className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
                data-testid="share-event-btn">
                <Share2 size={14} /> Share Event
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Banner Image (if exists - show prominently below hero) */}
      {event.cover_image && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-6 relative z-10">
          <div className="rounded-lg overflow-hidden shadow-xl border border-[#E4E4E7]">
            <img
              src={event.cover_image}
              alt={`${event.title} banner`}
              className="w-full h-48 sm:h-64 md:h-80 object-cover"
              data-testid="event-banner-image"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-10">
            {event.under_review && (
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="event-under-review-banner">
                This event is being re-reviewed after an organizer update. Your team stays registered and you can keep managing it below.
              </div>
            )}
            {isTeam && !event.under_review && <HackathonShowcase eventId={event.id} title={event.title} />}
            {isTeam && <EventTeamPanel event={event} onTeamChange={setHasTeam} />}
            {/* Description */}
            {event.description && (
              <section>
                <h2 className="text-lg font-bold text-[#0A0A0A] uppercase tracking-[0.08em] mb-4 pb-2 border-b-2 border-[#0A0A0A]">About This Event</h2>
                <FormattedText text={event.description} className="text-[#52525B] leading-relaxed" />
              </section>
            )}

            {/* Page Sections */}
            {event.page_sections?.map((section, idx) => (
              <section key={section.id || idx} data-testid={`event-section-${idx}`}>
                <h2 className="text-lg font-bold text-[#0A0A0A] uppercase tracking-[0.08em] mb-4 pb-2 border-b-2 border-[#0A0A0A]">{section.title}</h2>
                <FormattedText text={section.content} className="text-[#52525B] leading-relaxed" />
              </section>
            ))}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            {/* Event Details Card */}
            <div className="bg-white rounded-lg border border-[#E4E4E7] p-6 sticky top-6">
              <h3 className="text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-4">Event Details</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar size={16} className="text-[#002FA7] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase tracking-[0.05em]">Date & Time</p>
                    <p className="text-sm font-medium text-[#0A0A0A]">{formatDate(event.event_date)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  {event.event_type === 'offline' ? <MapPin size={16} className="text-[#002FA7] mt-0.5 flex-shrink-0" /> : <Globe size={16} className="text-[#002FA7] mt-0.5 flex-shrink-0" />}
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase tracking-[0.05em]">Location</p>
                    <p className="text-sm font-medium text-[#0A0A0A]">{event.event_type === 'offline' ? event.venue || 'Venue TBD' : 'Virtual / Online'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Tag size={16} className="text-[#002FA7] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase tracking-[0.05em]">Price</p>
                    <p className="text-sm font-bold text-[#0A0A0A]" data-testid="event-price-detail">{event.is_paid ? `INR ${event.price?.toLocaleString()}` : 'Free'}{isTeam && event.is_paid && <span className="block text-xs font-medium text-[#52525B]">{event.team_payment_mode !== 'per_member' && event.team_fee_type === 'per_team' ? 'per team'
                      : event.team_payment_mode !== 'per_member' && event.team_fee_type === 'hybrid' ? `per member + INR ${(event.team_base_fee || 0).toLocaleString()} team fee` : 'per member'}</span>}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users size={16} className="text-[#002FA7] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase tracking-[0.05em]">Registrations</p>
                    <p className="text-sm font-medium text-[#0A0A0A]">{event.registration_count || 0} registered</p>
                  </div>
                </div>
                {event.company_name && (
                  <div className="flex items-start gap-3">
                    <Building2 size={16} className="text-[#002FA7] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#A1A1AA] uppercase tracking-[0.05em]">Organizer</p>
                      <div className="flex items-center gap-2 mt-1">
                        {event.company_logo && <img src={event.company_logo} alt="" className="w-6 h-6 rounded object-contain" />}
                        <p className="text-sm font-medium text-[#0A0A0A]">{event.company_name}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Register CTA in sidebar */}
              {!registered ? (
                <button onClick={openRegister}
                  className="w-full mt-6 bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold py-3 rounded text-sm transition-colors"
                  data-testid="sidebar-register-btn">
                  {isTeam ? teamCta : event.is_paid ? `Register - INR ${event.price?.toLocaleString()}` : 'Register Free'}
                </button>
              ) : (
                <div className="w-full mt-6 bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold py-3 rounded text-sm text-center flex items-center justify-center gap-2">
                  <CheckCircle size={16} /> You're Registered
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile sticky CTA */}
      {!registered && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-[#E4E4E7] py-3 px-4 z-40 lg:hidden">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <div>
              <p className="font-semibold text-[#0A0A0A] text-sm truncate max-w-[200px]">{event.title}</p>
              <p className="text-xs text-[#A1A1AA]">{event.is_paid ? `INR ${event.price?.toLocaleString()}` : 'Free'}</p>
            </div>
            <button onClick={openRegister}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 rounded font-medium text-sm transition-colors"
              data-testid="sticky-register-btn">
              Register
            </button>
          </div>
        </div>
      )}

      {/* Success Ticket */}
      {registered && regData && !isTeam && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Ticket className="text-emerald-600" size={20} />
            </div>
            <div>
              <h3 className="text-base font-bold text-[#0A0A0A]">Registration Confirmed</h3>
              <p className="text-xs text-[#A1A1AA]">Download or share your ticket for {event.title}</p>
            </div>
          </div>
          <div data-testid="registration-ticket">
            <EventTicket event={event} registration={ticketReg || regData} />
          </div>
        </div>
      )}

      {/* Registration Modal */}
      {showRegister && (
        <RegistrationModal event={event} onClose={() => setShowRegister(false)}
          onRegistered={(reg) => { setRegistered(true); setRegData(reg); setEvent((e) => ({ ...e, registration_count: (e.registration_count || 0) + 1 })); setShowRegister(false); toast.success('Registered!'); }} />
      )}
    </div>
  );
};


// Registration Modal
// Project details are collected on the hackathon day, never at registration.
const PROJECT_FIELD_RX = /\b(project|idea|github|repo(sitory)?|demo|prototype|solution|tech ?stack|problem statement)\b/i;
const registrationFields = (event) => {
  const fields = event.custom_registration_fields || [];
  const isHackathon = event.registration_mode === 'team' || /hackathon/i.test(`${event.category || ''} ${event.title || ''}`);
  return isHackathon ? fields.filter((f) => !PROJECT_FIELD_RX.test(f.label || '')) : fields;
};

const RegistrationModal = ({ event, onClose, onRegistered }) => {
  const [mode, setMode] = useState('guest');
  const [form, setForm] = useState({ name: '', email: '', phone: '', college: '' });
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const codearenaToken = localStorage.getItem('codearena_token');
  const customFields = registrationFields(event);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null); // {discount_amount, final_price, code}
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const [quote, setQuote] = useState(null);
  useEffect(() => {
    if (!event.is_paid) return;
    axios.get(`${API}/api/public/events/${event.id}/price-quote`).then((r) => setQuote(r.data)).catch(() => {});
  }, [event.id, event.is_paid]);
  const breakdown = coupon?.price_breakdown || quote;
  const payable = breakdown ? breakdown.total : coupon ? coupon.final_price : (event.price || 0);

  const validateCoupon = async () => {
    const code = couponCode.trim();
    if (!code) { setCouponError('Enter a coupon code'); return; }
    setValidatingCoupon(true);
    setCouponError('');
    try {
      const params = new URLSearchParams({ coupon_code: code });
      if (form.email) params.append('email', form.email);
      const res = await axios.post(`${API}/api/public/events/${event.id}/validate-coupon?${params.toString()}`);
      setCoupon(res.data);
      toast.success(res.data.message || 'Coupon applied');
    } catch (err) {
      setCoupon(null);
      setCouponError(err.response?.data?.detail || 'Invalid coupon code');
    } finally { setValidatingCoupon(false); }
  };

  const removeCoupon = () => { setCoupon(null); setCouponCode(''); setCouponError(''); };

  const initiateCashfreePayment = async (registrationId) => {
    try {
      const q = coupon?.code ? `&coupon_code=${encodeURIComponent(coupon.code)}` : '';
      const res = await axios.post(`${API}/api/public/events/${event.id}/create-payment?registration_id=${registrationId}${q}`);
      if (res.data.payment_session_id) {
        const { load } = await import('@cashfreepayments/cashfree-js');
        const cashfree = await load({ mode: 'production' });
        cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          returnUrl: `${window.location.origin}/events/${event.id}?order_id=${res.data.order_id}&status=success`
        });
      } else {
        // Fully discounted (free via coupon) or mock — confirmed without gateway
        toast.success('Registration confirmed!');
        onRegistered({ id: registrationId, name: form.name || 'User', email: form.email || '', payment_status: 'paid' });
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Payment failed'); }
  };

  const handleGuestRegister = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email required'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/public/events/${event.id}/register-guest`, {
        ...form,
        custom_fields: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined
      });
      const reg = res.data.registration;
      if (event.is_paid && reg.payment_status === 'pending') {
        toast.info('Redirecting to payment...');
        await initiateCashfreePayment(reg.id);
      } else { onRegistered(reg); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Registration failed'); }
    finally { setSubmitting(false); }
  };

  const handleCodeArenaRegister = async () => {
    if (!codearenaToken) { toast.error('Please login first'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/public/events/${event.id}/register-codearena`, {}, {
        headers: { Authorization: `Bearer ${codearenaToken}` }
      });
      const reg = res.data.registration;
      if (event.is_paid && reg.payment_status === 'pending') {
        toast.info('Redirecting to payment...');
        await initiateCashfreePayment(reg.id);
      } else { onRegistered(reg); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Registration failed'); }
    finally { setSubmitting(false); }
  };

  // Coupon UI block (paid events only)
  const CouponBlock = () => (
    <div className="rounded-lg border border-dashed border-[#E4E4E7] p-3.5 bg-[#FAFAFA]" data-testid="coupon-block">
      <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-2 flex items-center gap-1.5"><Tag size={13} /> Apply Coupon Code</label>
      {coupon ? (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-bold text-emerald-700 font-mono">{coupon.code}</p>
            <p className="text-xs text-emerald-600">You save INR {Number(coupon.discount_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
          </div>
          <button type="button" onClick={removeCoupon} data-testid="remove-coupon-btn" className="text-xs font-semibold text-red-500 hover:text-red-600">Remove</button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input value={couponCode} onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
            placeholder="Enter code" data-testid="coupon-input"
            className="flex-1 px-3 py-2 border border-[#E4E4E7] rounded text-sm uppercase focus:outline-none focus:border-[#002FA7]" />
          <button type="button" onClick={validateCoupon} disabled={validatingCoupon} data-testid="apply-coupon-btn"
            className="px-4 py-2 bg-[#0A0A0A] hover:bg-[#1f1f1f] text-white rounded text-sm font-semibold disabled:opacity-50 whitespace-nowrap">
            {validatingCoupon ? '...' : 'Apply'}
          </button>
        </div>
      )}
      {couponError && <p className="text-xs text-red-500 mt-2" data-testid="coupon-error">{couponError}</p>}
      {breakdown && <div className="mt-3"><PriceBreakdown b={breakdown} baseLabel="Event fee" /></div>}
      {coupon && !breakdown && (
        <div className="mt-3 pt-3 border-t border-[#E4E4E7] space-y-1 text-sm" data-testid="coupon-summary">
          <div className="flex justify-between text-[#52525B]"><span>Event fee</span><span className="line-through">INR {event.price?.toLocaleString()}</span></div>
          <div className="flex justify-between text-emerald-600"><span>Discount</span><span>− INR {Math.round(coupon.discount_amount).toLocaleString()}</span></div>
          <div className="flex justify-between font-bold text-[#0A0A0A]"><span>Payable</span><span data-testid="payable-amount">INR {Number(payable).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
        </div>
      )}
    </div>
  );

  const payLabel = payable <= 0 ? 'Register Free' : `Pay INR ${Number(payable).toLocaleString('en-IN', { maximumFractionDigits: 2 })} & Register`;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="registration-modal" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E4E4E7]">
          <h3 className="text-base font-bold text-[#0A0A0A]">Register for {event.title}</h3>
          {event.is_paid && <p className="text-sm text-[#A1A1AA] mt-1">Fee: INR {event.price?.toLocaleString()}</p>}
        </div>

        <div className="p-6">
          <div className="flex rounded bg-[#F4F4F5] p-1 mb-6">
            <button onClick={() => setMode('guest')}
              className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${mode === 'guest' ? 'bg-white shadow-sm text-[#0A0A0A]' : 'text-[#A1A1AA]'}`}
              data-testid="guest-register-tab">Guest</button>
            <button onClick={() => setMode('codearena')}
              className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${mode === 'codearena' ? 'bg-white shadow-sm text-[#0A0A0A]' : 'text-[#A1A1AA]'}`}
              data-testid="codearena-register-tab">EvalentX Arena</button>
          </div>

          {mode === 'guest' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-1.5">Full Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20" placeholder="John Doe" data-testid="guest-name-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-1.5">Email *</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20" placeholder="john@example.com" data-testid="guest-email-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-1.5">Phone</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20" placeholder="+91 98765 43210" data-testid="guest-phone-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-1.5">College / Org</label>
                <input value={form.college} onChange={e => setForm({ ...form, college: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20" placeholder="University name" />
              </div>
              {/* Custom Registration Fields */}
              {customFields.map((field, idx) => (
                <div key={idx}>
                  <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em] mb-1.5">
                    {field.label} {field.required && '*'}
                  </label>
                  <input
                    type={field.type || 'text'}
                    value={customFieldValues[field.label] || ''}
                    onChange={e => setCustomFieldValues(prev => ({ ...prev, [field.label]: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/20"
                    placeholder={field.placeholder || field.label}
                    required={field.required}
                  />
                </div>
              ))}
              {event.is_paid && <CouponBlock />}
              <button onClick={handleGuestRegister} disabled={submitting}
                className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-3 rounded font-semibold text-sm disabled:opacity-50 transition-colors" data-testid="submit-guest-register">
                {submitting ? 'Registering...' : event.is_paid ? payLabel : 'Register (Free)'}
              </button>
            </div>
          ) : (
            <div className="py-6">
              {codearenaToken ? (
                <div className="text-center">
                  <CheckCircle className="mx-auto text-emerald-500 mb-3" size={36} />
                  <p className="text-sm text-[#52525B] mb-4">Logged in. Your profile will be used.</p>
                  {event.is_paid && <div className="text-left mb-4"><CouponBlock /></div>}
                  <button onClick={handleCodeArenaRegister} disabled={submitting}
                    className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-3 rounded font-semibold text-sm disabled:opacity-50 transition-colors" data-testid="submit-codearena-register">
                    {submitting ? 'Registering...' : event.is_paid ? payLabel : 'Register with EvalentX Arena'}
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-[#52525B] mb-4">Login to register with your verified profile.</p>
                  <a href="/codearena/auth" className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white px-6 py-3 rounded font-medium text-sm transition-colors">
                    Login to EvalentX Arena <ArrowRight size={16} />
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-[#E4E4E7] flex justify-end">
          <button onClick={onClose} className="text-sm text-[#A1A1AA] hover:text-[#0A0A0A] font-medium">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default EventPage;
